import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Timer, 
  Target, 
  RotateCcw, 
  Keyboard,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Sun,
  Moon,
  ArrowBigLeftDash,
  Zap
} from "lucide-react";
import './TypeHome.css';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const defaultParagraphs = [
  "As the sun slowly dipped below the horizon, the sky transformed into a canvas of amber, rose, and fading gold. A quiet calm settled over the landscape, and the noise of the day softened into distant echoes. In that gentle transition between light and darkness, there was a reminder that endings are rarely abrupt. They arrive gradually, giving us time to reflect, to breathe deeply, and to prepare ourselves for what tomorrow might quietly bring.",
  "True innovation rarely begins with resources or recognition; it begins with curiosity and the courage to question what already exists. Leaders are not defined by titles but by their willingness to think independently and act decisively. They challenge assumptions, refine ideas through failure, and remain consistent when progress feels slow. Over time, small disciplined efforts compound into meaningful results. The difference between ordinary and exceptional performance often lies in persistence rather than talent alone.",
  "Climbing a mountain is less about conquering the peak and more about enduring the journey upward. Each step demands patience, balance, and steady focus, especially when the path becomes steep and uncertain. Progress may feel slow, but every measured movement forward builds resilience. When the summit finally comes into view, the reward is not only the breathtaking scenery, but the quiet realization that strength is developed in moments of difficulty, not comfort.",
  "Books have a remarkable way of reflecting our inner thoughts while expanding our understanding of the world. Within their pages, we encounter perspectives that challenge assumptions and stories that stir empathy. Reading encourages focus in an age of distraction and nurtures imagination in a world driven by speed. Over time, the habit of thoughtful reading shapes communication, sharpens critical thinking, and strengthens the ability to express ideas with clarity and confidence."
];

export default function TypeHome() {
    const [theme, setTheme] = useState(() => {
      return localStorage.getItem("typeace-theme") || "light";
    });
    const [text, setText] = useState("");
    const [input, setInput] = useState("");
    const [isActive, setIsActive] = useState(false);
    const [wpm, setWpm] = useState(0);
    const [accuracy, setAccuracy] = useState(100);
    const [correctChars, setCorrectChars] = useState(0);
    const [incorrectChars, setIncorrectChars] = useState(0);
    const [showResults, setShowResults] = useState(false);
    const [fileName, setFileName] = useState("No file chosen");
    const [completedWords, setCompletedWords] = useState(0);
    const textareaRef = useRef(null);
    const timerRef = useRef(null);
    const fileInputRef = useRef(null);
    const textDisplayRef = useRef(null);
    const timeOptions = [60, 120, 300, 600, 1200, 1800];
    const [selectedTime, setSelectedTime] = useState(60);
    const [timeLeft, setTimeLeft] = useState(selectedTime);
    const navi = useNavigate();

    const themes = ["light", "dark", "neon"];
    const cycleTheme = () => {
      const currentIndex = themes.indexOf(theme);
      const nextIndex = (currentIndex + 1) % themes.length;
      setTheme(themes[nextIndex]);
    };
  
    useEffect(() => {
      setTimeLeft(selectedTime);
    }, [selectedTime]);
  
    useEffect(() => {
      const savedTheme = localStorage.getItem("typeace-theme");
      if (savedTheme) {
        setTheme(savedTheme);
      }
    }, []);
  
    useEffect(() => {
      document.documentElement.setAttribute("data-theme", theme);
      localStorage.setItem("typeace-theme", theme);
    }, [theme]);
  
    useEffect(() => {
      if (!textDisplayRef.current) return;
      const currentChar = textDisplayRef.current.querySelector(".char.current");
      if (currentChar) {
        currentChar.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    }, [input]);
  
    useEffect(() => {
      resetTest();
    }, []);
  
    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, []);
  
    useEffect(() => {
      if (isActive && timeLeft > 0) {
        timerRef.current = setTimeout(() => {
          setTimeLeft(prev => prev - 1);
        }, 1000);
      } else if (timeLeft === 0 && isActive) {
        calculateResults();
        setIsActive(false);
        setShowResults(true);
      }
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, [isActive, timeLeft]);
  
    const calculateAccuracy = useCallback((value) => {
      let correct = 0;
      let incorrect = 0;
      for (let i = 0; i < value.length; i++) {
        if (i < text.length) {
          if (value[i] === text[i]) {
            correct++;
          } else {
            incorrect++;
          }
        }
      }
      setCorrectChars(correct);
      setIncorrectChars(incorrect);
      const total = correct + incorrect;
      const acc = total === 0 ? 100 : (correct / total) * 100;
      setAccuracy(Number(acc.toFixed(1)));
    }, [text]);
  
    const calculateWPM = useCallback((value) => {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      const minutes = (selectedTime - timeLeft) / 60;
      const rawWPM = minutes > 0 ? Math.round(words.length / minutes) : 0;
      setWpm(rawWPM);
    }, [timeLeft]);
  
    const handleChange = (e) => {
      const value = e.target.value;
      
      if (!isActive && value.length > 0) {
        setIsActive(true);
      }
      
      if (value.length <= text.length) {
        setInput(value);
        calculateAccuracy(value);
        calculateWPM(value);
        const typedWords = value.trim().split(/\s+/);
        const textWords = text.split(/\s+/);
        let completed = 0;
        for (let i = 0; i < typedWords.length; i++) {
          if (typedWords[i] === textWords[i]) {
            completed++;
          } else {
            break;
          }
        }
        setCompletedWords(completed);
      }
    };
  
    const calculateResults = () => {
      calculateWPM(input);
      calculateAccuracy(input);
      setShowResults(true);
    };
  
    const resetTest = (newText = null) => {
      setInput("");
      setTimeLeft(selectedTime);
      setIsActive(false);
      setWpm(0);
      setAccuracy(100);
      setCorrectChars(0);
      setIncorrectChars(0);
      setShowResults(false);
      setCompletedWords(0);
      if (newText) {
        setText(newText);
      } else {
        const randomIndex = Math.floor(Math.random() * defaultParagraphs.length);
        setText(defaultParagraphs[randomIndex]);
      }
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };
  
    const handleFileUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          resetTest(content);
        };
        reader.readAsText(file);
      }
    };
  
    const triggerFileInput = () => {
      fileInputRef.current.click();
    };
  
    const getCharacterClass = (index) => {
      if (index >= input.length) return "upcoming";
      if (input[index] === text[index]) return "correct";
      return "incorrect";
    };
  
    const renderTextWithProgression = () => {
      return text.split("").map((char, index) => {
        let className = "char";
        if (index < input.length) {
          className += ` ${getCharacterClass(index)}`;
        } else if (index === input.length) {
          className += " current";
        } else {
          className += " upcoming";
        }
        if (char === ' ') {
          className += " space";
        }
        return (
          <span key={index} className={className}>
            {char === ' ' ? '␣' : char}
          </span>
        );
      });
    };
  
    const getProgressPercentage = () => {
      return Math.min(100, (input.length / text.length) * 100);
    };
  
    const getTimeColor = () => {
      if (timeLeft > 30) return "#6a8aab";
      if (timeLeft > 10) return "#e67e22";
      return "#c0392b";
    };
  
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
      <div className="app">
        <div className="container">
          <header className="header">
            <button onClick={() => navi("/form")} className="upload-btn"><ArrowBigLeftDash size={18}/></button>
            <button onClick={cycleTheme} className="theme-toggle">
              {theme === "light" && <Sun size={18} />}
              {theme === "dark" && <Moon size={18} />}
              {theme === "neon" && <Zap size={18} />}
            </button>
            <div className="header-content">
              <Keyboard size={28} className="header-icon" />
              <h1>TypeAce - Classic</h1>
            </div>
            <div className="header-actions">
              <span className="file-name">{fileName}</span>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".txt,.doc,.docx,.md"
                style={{ display: 'none' }}
              />
              <button onClick={triggerFileInput} className="upload-btn">
                <Upload size={18} />
                upload
              </button>
            </div>
          </header>
          <div className="timer-selector">
            {timeOptions.map((time) => (
              <button
                key={time}
                onClick={() => {
                  setSelectedTime(time);
                  setTimeLeft(time);
                  resetTest();
                }}
                className={`timer-btn ${selectedTime === time ? "active" : ""}`}
              >
                {time / 60}m
              </button>
            ))}
          </div>
          <div className="stats-bar">
            <div className="stat-item">
              <Timer size={16} />
              <span style={{ color: getTimeColor() }}>{timeLeft}s</span>
            </div>
            <div className="stat-item">
              <Target size={16} />
              <span>{wpm} wpm</span>
            </div>
            <div className="stat-item">
              <FileText size={16} />
              <span>{completedWords}/{text.split(/\s+/).length} words</span>
            </div>
            <div className="stat-item">
              <CheckCircle size={16} color="#27ae60" />
              <span>{correctChars}</span>
            </div>
            <div className="stat-item">
              <XCircle size={16} color="#c0392b" />
              <span>{incorrectChars}</span>
            </div>
          </div>
          <div className="text-container">
            <div className="text-display" ref={textDisplayRef}>
              {renderTextWithProgression()}
            </div>
            <div className="progress-indicator">
              <div 
                className="progress-line" 
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleChange}
            disabled={timeLeft === 0 || input.length === text.length}
            placeholder={timeLeft === 0 ? "Test completed" : "Start typing here..."}
            className={`typing-area ${timeLeft === 0 ? 'disabled' : ''}`}
            spellCheck={false}
          />
          {showResults && (
            <div className="results-panel">
              <h3>test complete</h3>
              <div className="results-grid">
                <div className="result-item">
                  <span className="result-label">wpm</span>
                  <span className="result-value">{wpm}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">accuracy</span>
                  <span className="result-value">{accuracy}%</span>
                </div>
                <div className="result-item">
                  <span className="result-label">correct</span>
                  <span className="result-value correct">{correctChars}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">incorrect</span>
                  <span className="result-value incorrect">{incorrectChars}</span>
                </div>
                <div className="result-item">
                  <span className="result-label">time</span>
                  <span className="result-value">
                    {selectedTime >= 60 ? selectedTime / 60 + "m" : selectedTime + "s"}
                  </span>
                </div>
                <div className="result-item">
                  <span className="result-label">words</span>
                  <span className="result-value">{completedWords}</span>
                </div>
              </div>
            </div>
          )}
          <div className="actions">
            <button onClick={resetTest} className="restart-btn">
              <RotateCcw size={16} />
              new test
            </button>
          </div>
          <div className="footer-tip">
            <span>⌘ + R to restart • upload .txt files</span>
          </div>
        </div>
      </div>
      </motion.div>
    );
}